import { isCanonicalConsentQuestion } from "./consentQuestionClassification";
import { authorityStateForQuestion } from "./authorityProgress";
import { TREATMENT_RUNTIME_STATE } from "./treatmentApiConstants";
import { projectVisibleProgramResponses } from "./questionVisibility";

const sourceId = (question) => String(question.source_id || question.id);
const canonicalChoiceScalar = (value) => {
  if (!value || typeof value !== "object") return value;
  const key = ["value", "code", "option_id", "id"].find(
    (candidate) => value[candidate] !== undefined
      && value[candidate] !== null
      && value[candidate] !== "",
  );
  return key ? value[key] : "";
};
const SINGLE_CHOICE_TYPES = new Set([
  "single", "single_choice", "yes_no", "select", "dropdown", "labs", "sex",
]);
const MULTIPLE_CHOICE_TYPES = new Set(["multiple", "multiple_choice"]);

export const canonicalConsentAnswer = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const agreed = value.agreed ?? value.accepted;
  if (typeof agreed !== "boolean") return value;
  return {
    agreed,
    consent_text: String(value.consent_text || "").trim(),
    ...(value.selected_value !== undefined && value.selected_value !== null
      ? { selected_value: value.selected_value }
      : {}),
    ...(value.consent_type ? { consent_type: value.consent_type } : {}),
    ...(value.consent_header ? { consent_header: value.consent_header } : {}),
  };
};

export const canonicalProgramQuestionAnswer = (question, value) => {
  const questionType = String(
    question?.question_type || question?.kind || "",
  ).trim().toLowerCase();
  if (SINGLE_CHOICE_TYPES.has(questionType)) return canonicalChoiceScalar(value);
  if (MULTIPLE_CHOICE_TYPES.has(questionType)) {
    const values = Array.isArray(value) ? value : [value];
    return values.map(canonicalChoiceScalar);
  }
  if (questionType === "consent") return canonicalConsentAnswer(value);
  return value;
};

export const hasCanonicalProgramDisqualifier = (question) => Boolean(
  (question?.validation_rules || question?.validation || {}).disqualifier
  || (question?.answer_choices || question?.choices || []).some(
    (choice) => choice && typeof choice === "object" && (
      choice.disqualifies === true
      || choice.is_disqualifying === true
      || choice.isDisqualifying === true
    ),
  ),
);

export const requiresProgramAnswerCheckpoint = (question, contractVersion = null) => {
  // Library consents are submitted together at their own runtime boundary.
  // A Program checkpoint would reinstall the unchanged consent cursor and
  // rewind local navigation to the first form.
  if (
    authorityStateForQuestion(question) === TREATMENT_RUNTIME_STATE.consents
  ) return false;
  if (String(question?.question_type || question?.kind || "").trim()
    .toLowerCase() === "treatment_screening_intro") return false;
  return (
    contractVersion === "1"
    || hasCanonicalProgramDisqualifier(question)
    || Boolean(question?.section_origin?.source_id)
    || ["bmi", "height_weight"].includes(String(
      question?.question_type || question?.kind || "",
    ).trim().toLowerCase())
  );
};

export const checkpointProgramIdentity = (sourceKind, runtimeProgramId) => (
  sourceKind === "program" ? String(runtimeProgramId || "") : ""
);

export const buildProgramAnswerContext = (
  questionnaire,
  responses,
  programId,
  currentQuestion = null,
) => {
  const targetProgramId = String(programId || "");
  if (!targetProgramId) return {};
  const questions = Array.isArray(questionnaire?.questions)
    ? questionnaire.questions
    : [];
  const currentIdentities = new Set([
    currentQuestion?.id,
    currentQuestion?.source_id,
    currentQuestion?.release_node_id,
  ].filter(Boolean).map(String));
  const currentIndex = currentIdentities.size
    ? questions.findIndex((question) => (
        [question?.id, question?.source_id, question?.release_node_id]
          .filter(Boolean)
          .some((identity) => currentIdentities.has(String(identity)))
      ))
    : -1;
  const visibleResponses = projectVisibleProgramResponses(
    questionnaire,
    responses,
  );

  return questions.reduce((answers, question, questionIndex) => {
    if (
      (currentIndex >= 0 && questionIndex >= currentIndex)
      || String(question?.program_id || "") !== targetProgramId
      || ["product_selection", "treatment_screening_intro"].includes(
        question?.question_type,
      )
      || isCanonicalConsentQuestion(question)
    ) return answers;
    const responseKey = [
      question?.id,
      question?.source_id,
      question?.release_node_id,
    ].find((key) => key != null && Object.prototype.hasOwnProperty.call(
      visibleResponses,
      key,
    ));
    const value = responseKey === undefined
      ? undefined
      : visibleResponses[responseKey];
    if (value === undefined || value === null || value === "") return answers;
    return {
      ...answers,
      [sourceId(question)]: canonicalProgramQuestionAnswer(question, value),
    };
  }, {});
};

export const buildProgramAnswerCheckpoint = (
  question,
  value,
  programAnswers = null,
  fallbackProgramId = "",
) => {
  if (
    authorityStateForQuestion(question) === TREATMENT_RUNTIME_STATE.consents
  ) return null;
  if (String(question?.question_type || question?.kind || "").trim()
    .toLowerCase() === "state_routing") return null;
  const programId = String(question?.program_id || fallbackProgramId || "");
  const questionSourceId = String(question?.source_id || question?.id || "");
  if (!programId || !questionSourceId) return null;
  if (String(question?.question_type || question?.kind || "").trim()
    .toLowerCase() === "treatment_screening_intro") return null;
  if (
    value === undefined
    || value === null
    || value === ""
    || (Array.isArray(value) && value.length === 0)
  ) return null;
  const canonicalAnswer = canonicalProgramQuestionAnswer(question, value);
  const answers = {
    ...(programAnswers && typeof programAnswers === "object" ? programAnswers : {}),
    ...(canonicalAnswer !== undefined && canonicalAnswer !== null && canonicalAnswer !== ""
      ? { [questionSourceId]: canonicalAnswer }
      : {}),
  };
  if (!Object.keys(answers).length) return null;
  return {
    ...(programAnswers && typeof programAnswers === "object"
      ? { checkpoint_source_id: questionSourceId }
      : {}),
    answers: { [programId]: answers },
  };
};

/**
 * The runtime can reject an upstream browser answer after its authoritative
 * visibility context changes (for example, a preview patient profile hides a
 * sex-specific question). Remove only the identities named by the server so
 * the valid current checkpoint can be retried without replaying stale state.
 */
export const recoverHiddenAnswerCheckpoint = ({
  questionnaire,
  responses,
  payload,
  currentQuestion,
  error,
}) => {
  if (error?.code !== "hidden_answer_rejected") return null;
  const hiddenSourceIds = new Set(
    (error?.details?.hidden || [])
      .map((item) => String(item?.source_id || ""))
      .filter(Boolean),
  );
  if (!hiddenSourceIds.size) return null;

  const hiddenResponseIds = new Set(hiddenSourceIds);
  (questionnaire?.questions || []).forEach((question) => {
    const identities = [
      question?.source_id,
      question?.id,
      question?.release_node_id,
    ].filter(Boolean).map(String);
    if (identities.some((identity) => hiddenSourceIds.has(identity))) {
      identities.forEach((identity) => hiddenResponseIds.add(identity));
    }
  });

  const nextResponses = Object.fromEntries(
    Object.entries(responses || {}).filter(
      ([identity]) => !hiddenResponseIds.has(String(identity)),
    ),
  );
  const nextAnswers = Object.fromEntries(
    Object.entries(payload?.answers || {}).map(([programId, answers]) => [
      programId,
      Object.fromEntries(
        Object.entries(answers || {}).filter(
          ([identity]) => !hiddenSourceIds.has(String(identity)),
        ),
      ),
    ]),
  );
  const checkpointSourceId = String(payload?.checkpoint_source_id || "");
  const currentIdentities = new Set([
    currentQuestion?.source_id,
    currentQuestion?.id,
    currentQuestion?.release_node_id,
  ].filter(Boolean).map(String));
  const currentWasRejected = [...currentIdentities].some(
    (identity) => hiddenSourceIds.has(identity),
  );
  const checkpointStillPresent = Object.values(nextAnswers).some(
    (answers) => Object.prototype.hasOwnProperty.call(
      answers || {},
      checkpointSourceId,
    ),
  );

  return {
    responses: nextResponses,
    payload: { ...payload, answers: nextAnswers },
    canRetry: !currentWasRejected && Boolean(checkpointSourceId) && checkpointStillPresent,
  };
};

export const buildBoundaryAnswerCheckpoint = (question, value) => {
  const questionSourceId = String(question?.source_id || question?.id || "");
  if (!questionSourceId) return null;
  return {
    checkpoint_source_id: questionSourceId,
    answers: {
      [questionSourceId]: canonicalProgramQuestionAnswer(question, value),
    },
  };
};
